import type { ReactNode } from 'react';

export const LoadingState = ({ label }: { label?: string }): ReactNode => (
  <div className="state" role="status" aria-live="polite" aria-busy="true">
    <span className="loading-spinner" aria-hidden="true" />
    <div style={{ marginTop: 12 }}>{label ?? 'Loading…'}</div>
  </div>
);

const TxRowSkeleton = (): ReactNode => (
  <div className="skeleton-tx-row" aria-hidden="true">
    <div className="skeleton-tx-left">
      <div className="skeleton skeleton-line w-3-4" />
      <div className="skeleton skeleton-line w-1-2 w-sm" />
    </div>
    <div className="skeleton-tx-right">
      <div className="skeleton skeleton-line w-1-3" style={{ width: 56 }} />
      <div className="skeleton skeleton-line w-sm" style={{ width: 36 }} />
    </div>
  </div>
);

export const TransactionListSkeleton = ({ rows = 6 }: { readonly rows?: number }): ReactNode => (
  <div role="status" aria-live="polite" aria-busy="true" aria-label="Loading activity">
    <span className="sr-only">Loading…</span>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} aria-hidden="true">
        <TxRowSkeleton />
        {i < rows - 1 ? <div className="hairline" /> : null}
      </div>
    ))}
  </div>
);

export const TopupLoadingSkeleton = (): ReactNode => (
  <main className="screen" role="status" aria-live="polite" aria-busy="true" aria-label="Loading top-up options">
    <span className="sr-only">Loading…</span>
    <div className="topbar" aria-hidden="true">
      <div className="skeleton skeleton-line" style={{ width: 32, height: 32, borderRadius: 8 }} />
      <div className="skeleton skeleton-line" style={{ width: 60, height: 11 }} />
      <div style={{ width: 32 }} />
    </div>
    <div aria-hidden="true" style={{ marginBottom: 24 }}>
      <div className="skeleton skeleton-eyebrow" style={{ marginBottom: 16 }} />
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <TxRowSkeleton />
          {i < 2 ? <div className="hairline" /> : null}
        </div>
      ))}
    </div>
  </main>
);

export const AccountLoadingSkeleton = (): ReactNode => (
  <div className="screen" role="status" aria-live="polite" aria-busy="true" aria-label="Loading account">
    <span className="sr-only">Loading…</span>
    <div className="topbar" aria-hidden="true">
      <div className="skeleton skeleton-line" style={{ width: 32, height: 32, borderRadius: 8 }} />
      <div className="skeleton skeleton-line" style={{ width: 80, height: 18, borderRadius: 6 }} />
      <div style={{ width: 32 }} />
    </div>
    <div className="skeleton" style={{ borderRadius: 14, height: 260, marginBottom: 16 }} aria-hidden="true" />
    <div className="skeleton" style={{ height: 46, borderRadius: 10 }} aria-hidden="true" />
  </div>
);

export const CardsDashboardSkeleton = (): ReactNode => (
  <div role="status" aria-live="polite" aria-busy="true" aria-label="Loading your cards">
    <span className="sr-only">Loading…</span>
    <div className="skeleton skeleton-card-face" aria-hidden="true" />
    <div className="skeleton skeleton-carousel" aria-hidden="true" />
    <div style={{ textAlign: 'center' }} aria-hidden="true">
      <div className="skeleton skeleton-hero-amount" />
      <div className="skeleton skeleton-hero-sub" />
    </div>
    <div className="skeleton-action-row" aria-hidden="true">
      <div className="skeleton skeleton-action-btn" />
      <div className="skeleton skeleton-action-btn" />
      <div className="skeleton skeleton-action-btn" />
    </div>
    <div style={{ marginBottom: 14 }} aria-hidden="true">
      <div className="skeleton skeleton-eyebrow" />
    </div>
    <div className="hairline" aria-hidden="true" />
    {[0, 1, 2].map((i) => (
      <div key={i}>
        <TxRowSkeleton />
        {i < 2 ? <div className="hairline" /> : null}
      </div>
    ))}
  </div>
);

export const ErrorState = ({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry?: () => void;
}): ReactNode => (
  <div className="state state--error" role="alert">
    <div>{message}</div>
    {onRetry !== undefined ? (
      <button type="button" className="retry" onClick={onRetry}>
        Retry
      </button>
    ) : null}
  </div>
);

export const EmptyState = ({ message }: { readonly message: string }): ReactNode => (
  <div className="empty-state" role="status">
    {message}
  </div>
);
